/**
 * This file is responsible for rendering the login page in the application.
 *
 * The component uses server-side rendering (getServerSideProps) to fetch the translations for the page.
 *
 *
 *
 * The Login component from '~/ui/pages/User/Login' is used to render the login form.
 *
 * If there's an error during server-side rendering, it is passed as a prop to the NextPage component and can be used to display an error message.
 *
 * The NextPage component from '~/ui/layouts/NextPage' is used to render the page layout.
 */

import { ReactElement } from 'react';
import { GetServerSideProps } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import { NextPage, LoginPage as Login } from '@Hotels-at-Home/hah-fe-core/dist/components';

import { TServerSideProps } from '@Hotels-at-Home/hah-fe-core/dist/types';
import { usePageViewEvent } from '@Hotels-at-Home/hah-fe-core/dist/hooks';

import Services from '~/ui/components/common/Services';
import { homeServicesMock } from '~/ui/components/common/Services/servicesMock';

const LoginPage = (props: TServerSideProps): ReactElement => {
  const { error } = props;

  const { t } = useTranslation('', { keyPrefix: '' });
  const { t: tLogin } = useTranslation('', { keyPrefix: '$local.$login' });

  usePageViewEvent();

  const meta = {
    title: `${t('$local.$meta.meta_title')} - ${t('$common.$labels.settings')} - ${t(
      '$common.$headings.loginHeading',
    )}`,
    description: `${t('$local.$meta.meta_title')} - ${t('$common.$labels.settings')} - ${t(
      '$common.$headings.loginHeading',
    )}`,
  };

  const REGISTER = '/user/register';

  return (
    <NextPage error={error} meta={meta}>
      <div className="account-container">
        <div className="img-wrap">
          <img src="/images/misc/filler.gif" alt="scent" width="100%" height="auto" />
        </div>
        <div className="login-container">
          <Login showHCaptcha />
          <div className="create-account-container">
            <p>
              {tLogin('newCostumer')}
              <a href={`${REGISTER}`}>{tLogin('createAccount')}</a>
            </p>
            <p>{tLogin('createAccountCopy')}</p>
          </div>
        </div>
      </div>
      <div className="login-services">
        <Services items={homeServicesMock} border={false} />
      </div>
    </NextPage>
  );
};

export const getServerSideProps: GetServerSideProps = async ctx => {
  const props: TServerSideProps = {
    ...(await serverSideTranslations(ctx.locale)),
    error: null,
  };

  return { props };
};

export default LoginPage;
